import { useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api.js';

export function ScopeJobForm({
  ehrId,
  defaultLoginUrl,
  onStarted,
  onCancel,
}: {
  ehrId: string;
  defaultLoginUrl?: string | null;
  onStarted: (scopeJobId: string) => void;
  onCancel?: () => void;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginUrl, setLoginUrl] = useState(defaultLoginUrl ?? '');
  const [remember, setRemember] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const { scopeJobId } = await api.startScopeJob(ehrId, {
        username,
        password,
        loginUrl: loginUrl || undefined,
        rememberCredentials: remember,
      });
      onStarted(scopeJobId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      toast.error(`Scope job failed: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="text-xs text-text-dimmed">
        Credentials are passed to the job runner only. If "remember for retry" is on, they're
        stored AES-256 encrypted and cleared after 30 days.
      </div>

      {!defaultLoginUrl && (
        <Field label="Login URL" hint="Override if build-specific">
          <input
            type="url"
            value={loginUrl}
            onChange={(e) => setLoginUrl(e.target.value)}
            placeholder="https://app.example.com/login"
            className="w-full bg-bg-primary border border-border rounded px-3 py-1.5 text-sm font-mono"
          />
        </Field>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Field label="Username">
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            className="w-full bg-bg-primary border border-border rounded px-3 py-1.5 text-sm"
            required
          />
        </Field>
        <Field label="Password">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className="w-full bg-bg-primary border border-border rounded px-3 py-1.5 text-sm"
            required
          />
        </Field>
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={remember}
          onChange={(e) => setRemember(e.target.checked)}
          className="rounded"
        />
        <span className="text-xs text-text-secondary">Remember for retry (encrypted, 30-day TTL)</span>
      </label>

      {error && (
        <div className="rounded border px-3 py-2 text-sm"
          style={{ borderColor: 'var(--accent-red)', background: 'var(--accent-red)11', color: 'var(--accent-red)' }}>
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-2">
        {onCancel && (
          <button type="button" onClick={onCancel}
            className="text-sm text-text-secondary hover:text-text-primary px-3 py-1.5">
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="rounded-xl px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
          style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
        >
          {submitting ? 'Queuing…' : 'Start scope job'}
        </button>
      </div>
    </form>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-text-secondary mb-1">{label}</div>
      {children}
      {hint && <div className="text-xs text-text-dimmed mt-1">{hint}</div>}
    </label>
  );
}
